import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../services/firebase';
import {
  collection, query, orderBy, onSnapshot, doc, updateDoc, serverTimestamp, addDoc,
} from 'firebase/firestore';
import {
  MdInbox, MdCheckCircle, MdPending, MdPerson, MdSend, MdArrowBack,
} from 'react-icons/md';

const STATUS_COLORS = {
  open: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200', label: 'Open', dot: 'bg-blue-500' },
  in_progress: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200', label: 'In Progress', dot: 'bg-amber-500' },
  resolved: { bg: 'bg-emerald-100', text: 'text-emerald-700', border: 'border-emerald-200', label: 'Resolved', dot: 'bg-emerald-500' },
};

const CATEGORY_COLORS = {
  'Billing / Payment': 'bg-yellow-50 text-yellow-700 border-yellow-100',
  'Technical Problem': 'bg-red-50 text-red-700 border-red-100',
  'Content Issue': 'bg-purple-50 text-purple-700 border-purple-100',
  'Account Problem': 'bg-orange-50 text-orange-700 border-orange-100',
  'Feature Request': 'bg-teal-50 text-teal-700 border-teal-100',
  'General Issue': 'bg-gray-100 text-gray-600 border-gray-200',
  'Other': 'bg-gray-100 text-gray-600 border-gray-200',
};

const SupportInbox = () => {
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  
  const chatEndRef = useRef(null);

  useEffect(() => {
    const q = query(collection(db, 'support_messages'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setTickets(data);
      setLoading(false);
      if (selectedTicket) {
        const updated = data.find((t) => t.id === selectedTicket.id);
        if (updated) setSelectedTicket(updated);
      }
    });
    return () => unsub();
  }, [selectedTicket]);

  useEffect(() => {
    // Scroll to bottom when a ticket is selected or updated
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedTicket?.adminReply, selectedTicket?.id]);

  const filteredTickets = filterStatus === 'all'
    ? tickets
    : tickets.filter((t) => t.status === filterStatus);

  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedTicket) return;
    setSending(true);
    try {
      await updateDoc(doc(db, 'support_messages', selectedTicket.id), {
        adminReply: replyText.trim(),
        status: 'resolved',
        repliedAt: serverTimestamp(),
      });

      await addDoc(collection(db, 'notifications'), {
        title: 'Support Reply',
        message: `Admin has replied to your ticket: "${selectedTicket.subject}"`,
        type: 'info',
        action: 'support_reply',
        audience: selectedTicket.userId,
        timestamp: serverTimestamp(),
      });

      setReplyText('');
    } catch (err) {
      console.error('Error replying:', err);
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await updateDoc(doc(db, 'support_messages', ticketId), { status: newStatus });
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const formatDate = (ts) => {
    if (!ts?.toDate) return '';
    const d = ts.toDate();
    const today = new Date();
    const isToday = d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
    if (isToday) {
      return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    }
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const openCount = tickets.filter((t) => t.status === 'open').length;
  const inProgressCount = tickets.filter((t) => t.status === 'in_progress').length;
  const resolvedCount = tickets.filter((t) => t.status === 'resolved').length;

  return (
    <div className="w-full h-[calc(100vh-80px)] md:h-[calc(100vh-100px)] flex flex-col py-4 md:py-6 max-w-7xl mx-auto animate-fade-in">
      {/* Header & Stats */}
      <div className="flex flex-col md:flex-row md:items-center justify-start gap-4 md:gap-8 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold text-darkNavy tracking-tight">Support Inbox</h1>
          <p className="text-gray-500 font-medium mt-1">Manage and respond to user inquiries.</p>
        </div>
        <div className="grid grid-cols-3 gap-2 md:gap-3 w-full md:w-auto">
          {[
            { label: 'Open', count: openCount, color: 'text-blue-700', bg: 'bg-blue-50', activeBg: 'bg-blue-100 border-blue-300', status: 'open' },
            { label: 'In Progress', count: inProgressCount, color: 'text-amber-700', bg: 'bg-amber-50', activeBg: 'bg-amber-100 border-amber-300', status: 'in_progress' },
            { label: 'Resolved', count: resolvedCount, color: 'text-emerald-700', bg: 'bg-emerald-50', activeBg: 'bg-emerald-100 border-emerald-300', status: 'resolved' },
          ].map((stat) => (
            <button
              key={stat.status}
              onClick={() => setFilterStatus(filterStatus === stat.status ? 'all' : stat.status)}
              className={`flex flex-col items-center justify-center gap-1 md:flex-row md:justify-start md:gap-3 p-3 md:px-4 md:py-3 rounded-2xl border transition-all duration-300 ${
                filterStatus === stat.status ? stat.activeBg + ' shadow-sm transform scale-105' : stat.bg + ' border-transparent hover:brightness-95'
              }`}
            >
              <div className="flex flex-col items-center md:items-start">
                <span className={`text-xl sm:text-2xl font-black leading-none ${stat.color}`}>{stat.count}</span>
                <span className={`text-[10px] sm:text-xs font-bold uppercase tracking-wider opacity-80 mt-1 md:mt-0 ${stat.color} text-center md:text-left`}>{stat.label}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Main Layout */}
      <div className="flex-1 flex overflow-hidden bg-white rounded-3xl border border-gray-200 shadow-sm">
        
        {/* Sidebar - Ticket List */}
        <div className={`w-full md:w-[380px] flex-shrink-0 flex flex-col border-r border-gray-100 bg-gray-50/30 ${selectedTicket ? 'hidden md:flex' : 'flex'}`}>
          <div className="p-5 border-b border-gray-200 bg-white z-10 shadow-sm">
            <h2 className="text-lg font-bold text-darkNavy">
              {filterStatus === 'all' ? 'All Conversations' : STATUS_COLORS[filterStatus]?.label}
              <span className="ml-2 text-sm font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{filteredTickets.length}</span>
            </h2>
          </div>
          
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center p-12">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredTickets.length === 0 ? (
              <div className="p-12 text-center flex flex-col items-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <MdInbox size={32} className="text-gray-400" />
                </div>
                <p className="font-bold text-gray-500">Inbox Zero!</p>
                <p className="text-sm text-gray-400 mt-1">No tickets match this filter.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {filteredTickets.map((ticket) => {
                  const status = STATUS_COLORS[ticket.status] || STATUS_COLORS.open;
                  const isSelected = selectedTicket?.id === ticket.id;

                  return (
                    <button
                      key={ticket.id}
                      onClick={() => { setSelectedTicket(ticket); setReplyText(ticket.adminReply || ''); }}
                      className={`w-full text-left p-5 transition-all relative ${
                        isSelected ? 'bg-primary/5' : 'hover:bg-gray-50 bg-white'
                      }`}
                    >
                      {isSelected && <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-r-full" />}
                      <div className="flex justify-between items-start mb-2 gap-2">
                        <div className="flex items-center gap-2 overflow-hidden">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${status.dot}`} />
                          <h3 className="font-bold text-darkNavy text-sm truncate">{ticket.userName || 'Anonymous'}</h3>
                        </div>
                        <span className="text-xs font-semibold text-gray-400 flex-shrink-0">{formatDate(ticket.createdAt)}</span>
                      </div>
                      <p className="font-semibold text-gray-800 text-sm mb-1 truncate">{ticket.subject || 'No Subject'}</p>
                      <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">{ticket.message}</p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Chat Interface */}
        <div className={`flex-1 flex flex-col bg-[#F8FAFC] ${selectedTicket ? 'flex' : 'hidden md:flex'}`}>
          {selectedTicket ? (
            <>
              {/* Chat Header */}
              <div className="h-20 px-6 border-b border-gray-200 bg-white flex items-center justify-between shadow-sm z-10 flex-shrink-0">
                <div className="flex items-center gap-4">
                  <button onClick={() => setSelectedTicket(null)} className="md:hidden w-10 h-10 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-600 transition-colors">
                    <MdArrowBack size={24} />
                  </button>
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-lg shadow-sm">
                    {selectedTicket.userName?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div>
                    <h2 className="font-bold text-darkNavy flex items-center gap-2">
                      {selectedTicket.subject}
                    </h2>
                    <p className="text-xs text-gray-500 font-medium">
                      {selectedTicket.userName} • {selectedTicket.userEmail}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`hidden sm:inline-flex text-xs px-3 py-1 rounded-full font-bold border ${CATEGORY_COLORS[selectedTicket.category] || 'bg-gray-100 text-gray-600'}`}>
                    {selectedTicket.category}
                  </span>
                  <select
                    value={selectedTicket.status}
                    onChange={(e) => handleStatusChange(selectedTicket.id, e.target.value)}
                    className={`text-sm font-bold border rounded-xl px-4 py-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/40 transition-colors appearance-none ${STATUS_COLORS[selectedTicket.status]?.bg} ${STATUS_COLORS[selectedTicket.status]?.text} ${STATUS_COLORS[selectedTicket.status]?.border}`}
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>
              </div>

              {/* Chat Messages Area */}
              <div className="flex-1 overflow-y-auto p-6 space-y-6">
                
                <div className="flex justify-center mb-8">
                  <span className="text-xs font-bold text-gray-400 bg-gray-200/50 px-3 py-1 rounded-full uppercase tracking-wider">
                    {selectedTicket.createdAt?.toDate ? selectedTicket.createdAt.toDate().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }) : 'Conversation Started'}
                  </span>
                </div>

                {/* Incoming Bubble (User) */}
                <div className="flex flex-col items-start max-w-[85%]">
                  <span className="text-xs font-semibold text-gray-400 ml-2 mb-1">{selectedTicket.userName} • {formatDate(selectedTicket.createdAt)}</span>
                  <div className="bg-white border border-gray-200 text-darkNavy px-5 py-4 rounded-3xl rounded-tl-sm shadow-sm">
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{selectedTicket.message}</p>
                  </div>
                </div>

                {/* Outgoing Bubble (Admin) */}
                {selectedTicket.adminReply && (
                  <div className="flex flex-col items-end max-w-[85%] self-end ml-auto">
                    <span className="text-xs font-semibold text-gray-400 mr-2 mb-1">You • {formatDate(selectedTicket.repliedAt)}</span>
                    <div className="bg-gradient-to-br from-primary to-indigo-600 text-white px-5 py-4 rounded-3xl rounded-tr-sm shadow-md">
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{selectedTicket.adminReply}</p>
                    </div>
                  </div>
                )}
                
                <div ref={chatEndRef} />
              </div>

              {/* Chat Input Area */}
              <div className="p-4 bg-white border-t border-gray-200">
                <div className="flex items-end gap-3 bg-gray-50 border border-gray-200 rounded-3xl p-2 focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary transition-all shadow-sm">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply here..."
                    rows={Math.min(5, Math.max(1, replyText.split('\n').length))}
                    className="flex-1 resize-none bg-transparent px-4 py-3 text-sm focus:outline-none text-darkNavy max-h-32 overflow-y-auto"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendReply();
                      }
                    }}
                  />
                  <button
                    onClick={handleSendReply}
                    disabled={!replyText.trim() || sending}
                    className={`w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-full transition-all ${
                      replyText.trim() && !sending
                        ? 'bg-primary text-white hover:bg-indigo-600 shadow-md hover:shadow-lg transform hover:-translate-y-0.5'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {sending ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <MdSend size={20} className="ml-1" /> // ml-1 to visually center the send icon
                    )}
                  </button>
                </div>
                <p className="text-[10px] font-semibold text-gray-400 text-center mt-2 uppercase tracking-widest">
                  Press Enter to send, Shift+Enter for new line
                </p>
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8 text-center">
              <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-sm mb-6 border border-gray-100">
                <MdInbox size={48} className="text-gray-300" />
              </div>
              <h2 className="text-xl font-bold text-darkNavy mb-2">No Ticket Selected</h2>
              <p className="text-sm font-medium">Select a conversation from the sidebar to view details and reply.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportInbox;
